"use client";

import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Scale, Loader2, User } from "lucide-react";
import { API_ENDPOINTS } from "../../../constants/config";
import { getFullName } from "../../../utils/urlUtils";
import { toast } from "react-hot-toast";

export default function LeaveBalance({ employeeType: employeeTypeProp } = {}) {
  const outletContext = useOutletContext();
  const employeeType = employeeTypeProp || outletContext?.employeeType || "teacher";
  const isStaff = employeeType === "staff";
  const employeeLabel = isStaff ? "Staff" : "Teacher";
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [teacherId, setTeacherId] = useState(""); // "" = select first, "all" = all teachers, or actual id
  const [teachers, setTeachers] = useState([]);
  const [balance, setBalance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teachersLoading, setTeachersLoading] = useState(true);

  useEffect(() => {
    const fetchTeachers = async () => {
      setTeachersLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(isStaff ? API_ENDPOINTS.usersStaff : API_ENDPOINTS.usersTeachers, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.status === "success" && (data.data?.teachers || data.data?.staff)) setTeachers(isStaff ? data.data.staff : data.data.teachers);
      } catch (e) {
        toast.error("Failed to load employees");
      } finally {
        setTeachersLoading(false);
      }
    };
    setTeacherId("");
    setBalance([]);
    fetchTeachers();
  }, [employeeType]);

  const load = async () => {
    if (teacherId === "") return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let url = API_ENDPOINTS.leaveBalance(`year=${year}`);
      if (teacherId !== "all") url = API_ENDPOINTS.leaveBalance(`year=${year}&${isStaff ? "staffId" : "teacherId"}=${teacherId}`);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setBalance(data.data || []);
      else setBalance([]);
    } catch (e) {
      toast.error("Failed to load leave balance");
      setBalance([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (teacherId === "") setBalance([]);
    else load();
  }, [year, teacherId]);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const showAllColumns = teacherId === "all" && balance.length > 0;
  const rows = balance.flatMap((b) =>
    b.byLeaveType.map((lt) => ({
      ...lt,
      employeeCode: b.employeeCode,
      employeeName: b.employeeName,
      employeeId: isStaff ? b.staffId : b.teacherId,
    }))
  );

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-slate-50 border-b border-slate-200/80">
          <CardTitle className="flex items-center gap-2 text-slate-800 text-xl font-semibold">
            <Scale className="h-5 w-5 text-[#0263cc]" />
            Leave Balance
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Select a {employeeLabel.toLowerCase()} to see their leave balance for the year (used vs max per leave type). Leave types must have "Max days per year" set.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                <User className="h-3.5 w-3" /> {employeeLabel}
              </label>
              <Select value={teacherId} onValueChange={setTeacherId} disabled={teachersLoading}>
                <SelectTrigger className="w-[220px] bg-white">
                  <SelectValue placeholder={`Select ${employeeLabel.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All {employeeLabel.toLowerCase()}</SelectItem>
                  {teachers.map((t) => (
                    <SelectItem key={t._id} value={t._id}>
                      {getFullName(t)} {isStaff ? (t.staffId ? `(${t.staffId})` : "") : (t.employeeCode ? `(${t.employeeCode})` : "")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Year</label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-[100px] bg-white"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {teacherId === "" ? (
            <div className="py-12 text-center text-slate-500">
              Select a {employeeLabel.toLowerCase()} above to view leave balance.
            </div>
          ) : loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" /></div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-500">No leave balance data. Add leave types with max days per year and approved leaves.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    {showAllColumns && <TableHead className="font-semibold">Employee ID</TableHead>}
                    {showAllColumns && <TableHead className="font-semibold">Employee Name</TableHead>}
                    <TableHead className="font-semibold">Leave Type</TableHead>
                    <TableHead className="font-semibold">Used</TableHead>
                    <TableHead className="font-semibold">Max</TableHead>
                    <TableHead className="font-semibold">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={`${row.employeeId}-${row.leaveTypeId}-${idx}`} className="hover:bg-slate-50/50">
                      {showAllColumns && <TableCell className="font-medium">{row.employeeCode || "—"}</TableCell>}
                      {showAllColumns && <TableCell>{row.employeeName || "—"}</TableCell>}
                      <TableCell>{row.name} ({row.code})</TableCell>
                      <TableCell>{row.usedDays}</TableCell>
                      <TableCell>{row.maxDays ?? "—"}</TableCell>
                      <TableCell>
                        {row.balance != null ? (
                          <Badge className={row.balance >= 0 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>{row.balance}</Badge>
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
